/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { themr, type ThemeType } from 'react-css-themr';
import compose from 'recompose/compose';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Text } from '@locus-taxy/neo-ui';

import TerritoryActions from 'common/redux/actions/territoryMaster';
import ToastActions from 'common/redux/actions/toast';
import { dashboardColors } from 'common/enums/ColorEnum';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector as clientIdSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';

import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import TerritoryMasterSelectors from 'common/redux/selectors/territoryMaster';
import TerritoryTable from './TerritoryTable';

import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import { Checkbox } from 'common/components/core/checkbox';
import { withRouter } from 'common/components/core/router';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT,
    PaginatedTableWithExpander
} from 'common/components/core/table';
import SingleTerritoryEditContainer from './territoryCreation/SingleTerritoryEditContainer';
import VisitUtils from 'common/utils/VisitUtils';

import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { TeamModelType } from 'common/models/flow/Team';

import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/territoryMaster';
import { RouterLink } from 'common/components/core/link/RouterLink';

import theme from 'common/entity/BulkEditContainer.scss';

type Props = {
    t: TFunction,
    entitySettings: EntitySettings,
    selectedRows: Array<string>,
    toggleRowSelection: string => void,
    theme: ThemeType,
    teams: Immutable.List<TeamModelType>,
    isGlobalPersonnel: boolean,
    isPersonnelBlockingEnabled: boolean,
    clientId: string,
    fetchOptions: Object,
    unblockPersonnel: ({
        clientId: string,
        personnelId: string
    }) => Promise<$FixMe>
};

const DEFAULT_COLUMNS: Array<EntityColumnType> = Object.freeze([
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'masterTerritoryTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'masterTerritoryTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        //check
        Cell: props => {
            return (
                <Text truncated title={props.value}>
                    <RouterLink
                        to={{
                            search: `edit=${props.value}`
                        }}
                    >
                        {props.value}
                    </RouterLink>
                </Text>
            );
        }
    },
    {
        headerTextId: 'masterTerritoryTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'masterTerritoryTable.territoriesCount',
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: { territories }
            } = props;
            return territories.size;
        }
    },
    {
        headerTextId: 'masterTerritoryTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'masterTerritoryTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'masterTerritoryTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'masterTerritoryTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
]);

const TerritoryMaster = entityMasterFactory({
    PaginatedTableComponent: PaginatedTableWithExpander,
    EntityEditViewContainer: SingleTerritoryEditContainer,
    EntityImportContainer: null,
    EntityExportContainer: null,
    COLUMNS: DEFAULT_COLUMNS,
    options: {
        entityTableId: ENTITY_TABLE_ID
    }
});

const TerritoryMasterContainer = (props: Props): React.Node => {
    const { clientId, t } = props;

    const territoryMasterFetchOptions = {
        clientId
    };

    const getTdProps = (_, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { id } = rowInfo.original;
        const { selectedRows, toggleRowSelection, theme } = props;
        const isChecked = selectedRows.indexOf(id) >= 0;
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            t: props.t,
            entitySettings: props.entitySettings,
            theme: props.theme,
            isChecked,
            teams: props.teams,
            isGlobalPersonnel: props.isGlobalPersonnel,
            className: classnames({ [theme.selectedRow]: isChecked })
        };
    };

    const _renderSubTable = React.useCallback(row => {
        return <TerritoryTable data={row.original.territories} />;
    }, []);

    return (
        <TerritoryMaster
            {...props}
            entityNameId={ENTITY_NAME_ID}
            entityExportId={ENTITY_EXPORT_ID}
            emptyStateImageSrc="/img/masterTerritory-empty.svg"
            entityId="territoryId"
            entitySettingsName="territorySettings"
            pinTooltip={t('pinTooltip')}
            unpinTooltip={t('unpinTooltip')}
            fetchOptions={territoryMasterFetchOptions}
            hasFreezableColumns
            frozenColumnLimit={FROZEN_COLUMN_LIMIT}
            entityCanBeDisabled
            getTdProps={getTdProps}
            SubComponent={_renderSubTable}
        />
    );
};

const mapStateToProps = (state, ownProps) => {
    const data = TerritoryMasterSelectors.getEntities(state, ownProps);
    return {
        isLoading: TerritoryMasterSelectors.isEntitiesLoading(state, ownProps),
        data,
        isAddEntityActive: isAddEntityActiveSelector(state, ownProps),
        clientId: clientIdSelector(state, ownProps),
        teams: teamsSelector(state, ownProps),
        entityIdToEdit: editEntityIdSelector(state, ownProps),
        entitySettings: getEntitySettings(state, ownProps),
        tableSettings: getEntityTablePreferences(state, ENTITY_TABLE_ID),
        isImportEntityActive: isImportEntityActiveSelector(state, ownProps),
        isExportEntityActive: isExportEntityActiveSelector(state, ownProps),
        isGlobalPersonnel: isGlobalPersonnelSelector(state, ownProps)
    };
};

const mapActionsToProps = {
    fetchEntities: TerritoryActions.fetchActiveEntities,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    disableEntity: TerritoryActions.disableEntity,
    clearData: TerritoryActions.clearData,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    bulkDisableEntity: TerritoryActions.bulkDisableEntity
};

export default compose(
    themr(null, theme),
    translate('entityManagement'),
    withRouter,
    connect(mapStateToProps, mapActionsToProps),
    MasterContainerHOC
)(TerritoryMasterContainer);
